import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Modal, ModalBody, ModalHeader, ModalFooter, Button, Spinner, Alert, Input } from 'reactstrap';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import api from '../helpers/api';
import usePermission from '../Hooks/usePermission';
import { nowLocalInputValue, toBackendDateTime, assetStatusOptions } from '../constants/utils';

/**
 * Moves one or more assets to another location — either inside their own organization, or into a
 * different one.
 * The move is executed through the Equipment Planning module (POST equipment/asset-transfer),
 * so it appears as an entry in the Planning page / the asset's Planning tab. Permanent and
 * temporary (auto-returning) transfers are both supported, and a future start date schedules it.
 *
 * Choosing another organization as the destination turns this into a cross-organization transfer:
 * it is always permanent, always requires an administrator of the receiving organization to accept
 * it, and shows a server-computed preview of what would move, what would be detached, and anything
 * blocking it. The destination picker only appears for users who hold the transfer permission in
 * more than one organization.
 *
 * Two modes:
 *  - single: pass {@code asset} (the asset detail surfaces).
 *  - bulk:   pass {@code bulk} + {@code organizationId} (the Planning page) — the modal loads the
 *            org's assets for a multi-select and submits {@code assetIds}.
 *
 * Admins execute directly; Full Users submit a request that requires admin approval, reflected in
 * the confirm-button label and an inline note.
 *
 * <p>In bulk mode a caller that has already chosen the assets — the Assets list, where the user
 * ticks rows — passes {@code preselectedAssets} instead. The picker is then hidden and the
 * selection shown read-only, so there is only ever one place the set is chosen. The array must be
 * referentially stable (memoize it): the open effect depends on its identity.
 *
 * @param preselectedAssets optional option objects
 *        {@code { value, label, chipLabel, serialNumber, currentLocation, locationId }}
 */
const AssetTransferModal = ({ isOpen, toggle, asset, bulk = false, organizationId: orgIdProp, preselectedAssets, onSuccess }) => {
  const { t } = useTranslation();
  const { isAdmin } = usePermission();

  const [temporary, setTemporary] = useState(false);
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [selectedSubLocationId, setSelectedSubLocationId] = useState('');
  const [startDate, setStartDate] = useState(nowLocalInputValue());
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Bulk-mode asset picker: server-driven search (name / asset number / serial) + facet filters,
  // mirroring the Work Order form's canonical asset picker instead of loading the whole org tree.
  const [assetOptions, setAssetOptions] = useState([]);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [assetSearch, setAssetSearch] = useState('');
  const [assetFilters, setAssetFilters] = useState({ location: '', assetTypeId: '', assetStatus: '' });
  const [assetTypes, setAssetTypes] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  // Descendant ids per selected asset — a transfer takes the whole subtree, so the user is told
  // how many extra assets ride along before confirming.
  const [descendantsById, setDescendantsById] = useState({});

  // Cross-organization transfer: destinations the user may send assets to, the chosen one, and the
  // server's dry run of the move.
  const [destinations, setDestinations] = useState([]);
  const [destinationOrgId, setDestinationOrgId] = useState('');
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  // Monotonic id so only the newest preview response is applied.
  const previewRequestId = useRef(0);

  const organizationId = orgIdProp || asset?.organization?.id;
  // Seeded mode: the caller already chose the assets, so the in-modal picker is hidden.
  const seeded = bulk && Array.isArray(preselectedAssets) && preselectedAssets.length > 0;
  const isCrossOrg = !!destinationOrgId && String(destinationOrgId) !== String(organizationId);
  // Where the destination locations come from: another organization's locations are only readable
  // through the transfer-specific endpoint, which checks the permission for that organization.
  const locationsOrgId = isCrossOrg ? destinationOrgId : organizationId;

  const resetForm = useCallback(() => {
    setTemporary(false);
    setSelectedLocationId('');
    setSelectedSubLocationId('');
    setStartDate(nowLocalInputValue());
    setEndDate('');
    setReason('');
    setSelectedAssets([]);
    setAssetSearch('');
    setAssetFilters({ location: '', assetTypeId: '', assetStatus: '' });
    setDestinationOrgId('');
    setPreview(null);
    setError(null);
  }, []);

  // On open: reset, and load the organizations this user may transfer INTO. An empty list means
  // they only belong to one organization, and the destination picker stays hidden.
  useEffect(() => {
    if (!isOpen || !organizationId) return;
    resetForm();
    // Seed AFTER the reset, which unconditionally empties the selection on every open.
    if (seeded) setSelectedAssets(preselectedAssets);
    api
      .get('equipment/asset-transfer/destinations')
      .then(({ data }) => setDestinations(data?.response || []))
      .catch(() => setDestinations([]));

    // Asset types only feed the picker's facet filters, which a seeded modal does not show.
    if (bulk && !seeded) {
      api
        .get(`organization-asset-type/getAll?organizationId=${organizationId}`)
        .then(({ data }) => setAssetTypes(data?.response || []))
        .catch(() => setAssetTypes([]));
    }
  }, [isOpen, organizationId, bulk, seeded, preselectedAssets, resetForm]);

  // Load the destination's locations. Switching organization invalidates any location already
  // picked, so the selection is cleared rather than left pointing at the previous organization.
  useEffect(() => {
    if (!isOpen || !locationsOrgId) return;
    setSelectedLocationId('');
    setSelectedSubLocationId('');
    setLocations([]);
    setLoadingLocations(true);
    const request = isCrossOrg
      ? api.get('equipment/asset-transfer/destination-locations', { params: { organizationId: locationsOrgId } })
      : api.get(`organization-location/get-by-organization-id?organizationId=${locationsOrgId}&count=9999&hierarchy=false`);
    request
      .then(({ data }) => setLocations(data?.response?.[0]?.locations || []))
      .catch(() => setLocations([]))
      .finally(() => setLoadingLocations(false));
  }, [isOpen, locationsOrgId, isCrossOrg]);

  // A cross-org move is always permanent — clear any temporary selection when one is chosen.
  useEffect(() => {
    if (isCrossOrg && temporary) setTemporary(false);
  }, [isCrossOrg, temporary]);

  // Debounced option fetch. `search` matches name, asset number, serial number and manufacturer
  // server-side; `currentLocation` is a location NAME the backend resolves recursively, so a
  // parent location also matches assets in its sublocations. hierarchy:false flattens the tree,
  // making sub-assets selectable too.
  useEffect(() => {
    if (!isOpen || !bulk || seeded || !organizationId) return undefined;
    setLoadingAssets(true);
    const handle = setTimeout(() => {
      api
        .get('asset/all', {
          params: {
            organizationId,
            count: 20,
            hierarchy: false,
            ...(assetSearch ? { search: assetSearch } : {}),
            ...(assetFilters.location ? { currentLocation: assetFilters.location } : {}),
            ...(assetFilters.assetTypeId ? { assetTypeId: assetFilters.assetTypeId } : {}),
            ...(assetFilters.assetStatus ? { assetStatus: assetFilters.assetStatus } : {}),
          },
        })
        .then(({ data }) => setAssetOptions(
          (data?.response?.[0]?.assets || []).map((a) => ({
            value: a.id,
            label: a.assetNumber ? `${a.assetName} (ID: ${a.assetNumber})` : a.assetName,
            chipLabel: a.assetNumber ? `${a.assetName} (${a.assetNumber})` : a.assetName,
            serialNumber: a.serialNumber,
            currentLocation: a.currentLocation,
            locationId: a.locationId,
          }))
        ))
        .catch(() => setAssetOptions([]))
        .finally(() => setLoadingAssets(false));
    }, 300);
    return () => clearTimeout(handle);
  }, [isOpen, bulk, seeded, organizationId, assetSearch, assetFilters]);

  // Filterable location names: top-level locations plus their sublocations (path-labelled).
  const locationFilterOptions = locations.flatMap((l) => [
    { value: l.locationName, label: l.locationName },
    ...(l.subLocations || []).map((s) => ({ value: s.locationName, label: `${l.locationName} / ${s.locationName}` })),
  ]);
  // Decommissioned assets are excluded from asset/all, so that status filter option is pointless.
  const statusFilterOptions = assetStatusOptions(t)
    .filter((o) => o.value !== 'DECOMMISSIONED')
    .map((o) => ({ value: o.value, label: o.label }));
  const assetTypeFilterOptions = assetTypes.map((at) => ({ value: String(at.id), label: at.name }));

  const destinationOptions = locations.map((l) => ({ value: String(l.id), label: l.locationName }));

  // The current organization stays first in the list so "keep it here" is always an option.
  const destinationOrgOptions = [
    { value: String(organizationId), label: t('business.planning.this_organization', 'This organization') },
    ...destinations.map((d) => ({ value: String(d.id), label: d.name })),
  ];

  // Shrinks react-select to form-select-sm height so the filter row stays compact.
  const compactSelectStyles = {
    control: (base) => ({ ...base, minHeight: 31 }),
    valueContainer: (base) => ({ ...base, padding: '0 8px' }),
    dropdownIndicator: (base) => ({ ...base, padding: 4 }),
    clearIndicator: (base) => ({ ...base, padding: 4 }),
  };

  // Selected-asset pills: white text on the brand-blue background, mirroring the
  // canonical multi-value styling in Assets.jsx (black-on-blue was unreadable).
  const multiValueStyles = {
    multiValue: (base) => ({ ...base, backgroundColor: '#3980c0', borderRadius: '4px' }),
    multiValueLabel: (base) => ({ ...base, color: 'white', padding: '2px 6px' }),
    multiValueRemove: (base) => ({
      ...base,
      color: 'white',
      '&:hover': { backgroundColor: '#2e669a', color: 'white' },
    }),
  };

  const selectedLocation = locations.find((l) => String(l.id) === String(selectedLocationId));
  const subLocations = selectedLocation?.subLocations || [];
  const subLocationOptions = subLocations.map((s) => ({ value: String(s.id), label: s.locationName }));

  const handleLocationChange = (value) => {
    setSelectedLocationId(value);
    setSelectedSubLocationId('');
  };

  // The effective destination: a chosen sublocation, else the location itself.
  const destinationLocationId = Number(selectedSubLocationId || selectedLocationId) || null;

  // Assets already standing at the destination. The backend drops these from the move, so say so
  // before the user confirms rather than letting the result count come back lower than expected.
  const alreadyThere = useMemo(
    () => (destinationLocationId
      ? selectedAssets.filter((a) => a.locationId != null && Number(a.locationId) === destinationLocationId)
      : []),
    [selectedAssets, destinationLocationId]
  );
  const allAlreadyThere = bulk && selectedAssets.length > 0 && alreadyThere.length === selectedAssets.length;

  const selectedIdsKey = selectedAssets.map((a) => a.value).sort().join(',');
  // Fetch the subtree of every selected asset so the "N sub-assets move too" note is accurate.
  // Filtered list rows arrive with `children` empty, so this cannot be derived client-side.
  useEffect(() => {
    if (!isOpen || !bulk || !selectedIdsKey) {
      setDescendantsById({});
      return;
    }
    api
      .get('asset/descendant-ids', { params: { assetIds: selectedIdsKey } })
      .then(({ data }) => setDescendantsById(data?.response || {}))
      .catch(() => setDescendantsById({}));
  }, [isOpen, bulk, selectedIdsKey]);

  // Count only descendants that move *because of* their ancestor: an asset the user ticked in its
  // own right moves on its own row, and the subtree of an asset that will be skipped never moves.
  const movingSubAssetCount = useMemo(() => {
    const selectedIds = new Set(selectedAssets.map((a) => Number(a.value)));
    const skippedIds = new Set(alreadyThere.map((a) => Number(a.value)));
    const moving = new Set();
    Object.entries(descendantsById).forEach(([rootId, descendants]) => {
      if (skippedIds.has(Number(rootId))) return;
      (descendants || []).forEach((id) => {
        if (!selectedIds.has(Number(id))) moving.add(Number(id));
      });
    });
    return moving.size;
  }, [descendantsById, selectedAssets, alreadyThere]);

  const assetsChosen = bulk ? selectedAssets.length > 0 : !!asset?.id;
  const chosenAssetIds = bulk ? selectedAssets.map((a) => a.value) : (asset?.id ? [asset.id] : []);
  const destinationId = selectedSubLocationId || selectedLocationId;

  // Dry-run the move as soon as there is enough to describe it. Debounced, because the destination
  // and location selects change together while the user is still choosing.
  useEffect(() => {
    if (!isOpen || !isCrossOrg || !chosenAssetIds.length || !destinationId) {
      setPreview(null);
      setLoadingPreview(false);
      return undefined;
    }
    // Stamp each request and ignore anything but the latest. Without this a slow earlier request
    // can land after a newer one and replace a real blocker list with a stale "nothing blocks it",
    // which would enable the submit button for a selection the server will refuse.
    previewRequestId.current += 1;
    const requestId = previewRequestId.current;
    setLoadingPreview(true);
    const handle = setTimeout(() => {
      api
        .post('equipment/asset-transfer/preview', {
          assetIds: chosenAssetIds,
          locationId: Number(destinationId),
          temporary: false,
          destinationOrganizationId: Number(destinationOrgId),
        })
        .then(({ data }) => {
          if (previewRequestId.current === requestId) setPreview(data?.response || null);
        })
        .catch(() => {
          if (previewRequestId.current === requestId) setPreview(null);
        })
        .finally(() => {
          if (previewRequestId.current === requestId) setLoadingPreview(false);
        });
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isCrossOrg, destinationOrgId, destinationId, JSON.stringify(chosenAssetIds)]);

  const blockers = preview?.blockers || [];
  const detachedTotal = preview?.detachments
    ? (preview.detachments.contracts || 0) +
      (preview.detachments.certificates || 0) +
      (preview.detachments.warranties || 0) +
      (preview.detachments.spareParts || 0) +
      (preview.detachments.consumables || 0) +
      (preview.detachments.materiovigilance || 0)
    : 0;
  // A cross-org transfer may not be submitted until the server says nothing blocks it — the same
  // check runs again on submit and on approval, so this is a courtesy, not the enforcement.
  const canSubmit =
    assetsChosen &&
    !!selectedLocationId &&
    !!startDate &&
    (!temporary || !!endDate) &&
    !submitting &&
    !allAlreadyThere &&
    (!isCrossOrg || (!loadingPreview && !!preview && blockers.length === 0));

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await api.post('equipment/asset-transfer', {
        ...(bulk
          ? { assetIds: selectedAssets.map((a) => a.value) }
          : { assetId: asset.id }),
        locationId: destinationLocationId,
        temporary: isCrossOrg ? false : temporary,
        startDate: toBackendDateTime(startDate),
        endDate: !isCrossOrg && temporary ? toBackendDateTime(endDate) : null,
        reason: reason?.trim() || null,
        // Omitted for a within-org relocation, which keeps the existing behaviour untouched.
        ...(isCrossOrg ? { destinationOrganizationId: Number(destinationOrgId) } : {}),
      });
      if (onSuccess) onSuccess(data?.response, { scheduled });
      toggle();
    } catch (e) {
      // Bean-validation failures (e.g. over the bulk cap) put the per-field text in `warning`
      // — RestExceptionHandler uses warningWithError(status, null, errors, …) — while `message`
      // is only the generic "Validation failed."
      const fieldErrors = e?.response?.data?.warning;
      const firstFieldError = fieldErrors && typeof fieldErrors === 'object' && !Array.isArray(fieldErrors)
        ? Object.values(fieldErrors)[0]
        : null;
      // Bean-validation messages are declared as {message.key} and the app's validator does not
      // resolve them against the message bundle, so an unresolved key would otherwise be shown
      // to the user verbatim. Fall back to our own localized text in that case.
      const isUnresolvedKey = (v) => typeof v === 'string' && /^\{[\w.-]+\}$/.test(v.trim());
      const serverMessage = [firstFieldError, e?.response?.data?.message]
        .find((v) => typeof v === 'string' && v.trim() && !isUnresolvedKey(v));
      setError(
        serverMessage ||
          t('business.planning.transfer_failed', 'Failed to transfer the asset. Please try again.')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const scheduled = startDate && new Date(startDate) > new Date();

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle}>
        {isCrossOrg
          ? t('business.planning.transfer_to_organization', 'Transfer to Another Organization')
          : temporary
            ? t('business.planning.temporary_transfer', 'Temporary Transfer')
            : t('business.planning.permanent_transfer', 'Permanent Transfer')}
      </ModalHeader>
      <ModalBody>
        {error && <Alert color="danger">{error}</Alert>}

        {/* Cross-org always needs the RECEIVING organization to accept, whoever raised it */}
        {isCrossOrg ? (
          <Alert color="warning" className="py-2">
            {t(
              'business.planning.cross_org_needs_dest_approval',
              'This transfer must be approved by an administrator of {{organization}} before the assets move.',
              { organization: destinations.find((d) => String(d.id) === String(destinationOrgId))?.name || '' }
            )}
          </Alert>
        ) : (
          !isAdmin && (
            <Alert color="warning" className="py-2">
              {t('business.planning.transfer_needs_approval', 'This transfer will be submitted as a request and requires administrator approval.')}
            </Alert>
          )
        )}

        {/* Sub-assets travel with the parent — tell the user before they confirm */}
        {!bulk && asset?.children?.length > 0 && (
          <Alert color="info" className="py-2">
            {t(
              'business.planning.sub_assets_move_note',
              'This asset has {{count}} sub-asset(s) that will move with it.',
              { count: asset.children.length }
            )}
          </Alert>
        )}

        {/* Same disclosure for a multi-asset selection, counting each sub-asset once. */}
        {bulk && movingSubAssetCount > 0 && (
          <Alert color="info" className="py-2">
            {t(
              'business.planning.bulk_sub_assets_move_note',
              '{{count}} sub-asset(s) of the selected assets will move with them.',
              { count: movingSubAssetCount }
            )}
          </Alert>
        )}

        {/* Assets already at the chosen destination are dropped by the backend. */}
        {bulk && alreadyThere.length > 0 && !allAlreadyThere && (
          <Alert color="info" className="py-2">
            {t(
              'business.planning.already_at_destination_note',
              '{{count}} asset(s) already at this location will be skipped.',
              { count: alreadyThere.length }
            )}
          </Alert>
        )}
        {allAlreadyThere && (
          <Alert color="warning" className="py-2">
            {t(
              'business.planning.nothing_to_move',
              'All selected assets are already at this location. Choose a different destination.'
            )}
          </Alert>
        )}

        {/* Seeded mode: the caller chose the assets, so show them read-only. Changing the set
            means going back to the list — one selection surface, not two competing ones. */}
        {seeded && (
          <div className="mb-3">
            <label className="form-label">
              {t('business.planning.select_assets', 'Assets to transfer')} ({selectedAssets.length})
            </label>
            <div className="d-flex flex-wrap gap-1" style={{ maxHeight: 140, overflowY: 'auto' }}>
              {selectedAssets.map((a) => (
                <span
                  key={a.value}
                  className="badge"
                  style={{ backgroundColor: '#3980c0', color: 'white', fontWeight: 400 }}
                  title={[a.serialNumber, a.currentLocation].filter(Boolean).join(' • ')}
                >
                  {a.chipLabel || a.label}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Asset picker (bulk mode only): facet filters + server-side type-ahead */}
        {bulk && !seeded && (
          <div className="mb-3">
            <label className="form-label">{t('business.planning.select_assets', 'Assets to transfer')} *</label>
            {/* Labelled like the Assets filter modal — an unlabelled select showing a bare
                value ("B", "Online") reads as a form field of the transfer, not a filter. */}
            <div className="d-flex gap-2 mb-2">
              <div className="flex-fill">
                <label className="form-label small text-muted mb-1">{t('shared.location', 'Location')}</label>
                <Select
                  options={locationFilterOptions}
                  value={locationFilterOptions.find((o) => o.value === assetFilters.location) || null}
                  onChange={(selected) => setAssetFilters((prev) => ({ ...prev, location: selected?.value || '' }))}
                  isClearable
                  isSearchable
                  placeholder={t('shared.all_locations', 'All Locations')}
                  classNamePrefix="react-select"
                  styles={compactSelectStyles}
                  aria-label={t('shared.location', 'Location')}
                />
              </div>
              <div className="flex-fill">
                <label className="form-label small text-muted mb-1">{t('shared.asset_type', 'Asset Type')}</label>
                <Select
                  options={assetTypeFilterOptions}
                  value={assetTypeFilterOptions.find((o) => o.value === assetFilters.assetTypeId) || null}
                  onChange={(selected) => setAssetFilters((prev) => ({ ...prev, assetTypeId: selected?.value || '' }))}
                  isClearable
                  isSearchable
                  placeholder={t('shared.all_asset_types', 'All Asset Types')}
                  classNamePrefix="react-select"
                  styles={compactSelectStyles}
                  aria-label={t('shared.asset_type', 'Asset Type')}
                />
              </div>
              <div className="flex-fill">
                <label className="form-label small text-muted mb-1">{t('shared.status', 'Status')}</label>
                <Select
                  options={statusFilterOptions}
                  value={statusFilterOptions.find((o) => o.value === assetFilters.assetStatus) || null}
                  onChange={(selected) => setAssetFilters((prev) => ({ ...prev, assetStatus: selected?.value || '' }))}
                  isClearable
                  isSearchable
                  placeholder={t('shared.all_statuses', 'All Statuses')}
                  classNamePrefix="react-select"
                  styles={compactSelectStyles}
                  aria-label={t('shared.status', 'Status')}
                />
              </div>
            </div>
            <Select
              isMulti
              options={assetOptions}
              value={selectedAssets}
              onChange={(vals) => setSelectedAssets(vals || [])}
              onInputChange={(value, { action }) => {
                // react-select clears the visible input after a pick / on close — mirror that in
                // the search state or the options stay filtered by a term the user can't see.
                if (action === 'input-change') setAssetSearch(value);
                else if (action === 'set-value' || action === 'menu-close') setAssetSearch('');
              }}
              filterOption={() => true}
              isLoading={loadingAssets}
              formatOptionLabel={(opt, { context }) =>
                context === 'menu' ? (
                  <div>
                    <div>{opt.label}</div>
                    {(opt.serialNumber || opt.currentLocation) && (
                      <small className="text-muted">
                        {[opt.serialNumber, opt.currentLocation].filter(Boolean).join(' • ')}
                      </small>
                    )}
                  </div>
                ) : (
                  opt.chipLabel || opt.label
                )
              }
              noOptionsMessage={({ inputValue }) =>
                inputValue
                  ? t('shared.no_assets_found_matching', 'No assets found matching "{{searchTerm}}"', { searchTerm: inputValue })
                  : t('business.planning.select_assets_placeholder', 'Select one or more assets...')
              }
              placeholder={t('shared.type_to_search_assets', 'Type to search by name, ID, or serial number...')}
              classNamePrefix="react-select"
              styles={multiValueStyles}
            />
          </div>
        )}

        {/* Destination organization — only shown when the user may transfer into another one */}
        {destinations.length > 0 && (
          <div className="mb-3">
            <label className="form-label">{t('shared.organization', 'Organization')}</label>
            <Select
              options={destinationOrgOptions}
              value={destinationOrgOptions.find((o) => o.value === String(destinationOrgId || organizationId)) || null}
              onChange={(selected) => setDestinationOrgId(
                selected && String(selected.value) !== String(organizationId) ? selected.value : ''
              )}
              isSearchable
              placeholder={t('shared.select_organization', 'Select Organization')}
              classNamePrefix="react-select"
              aria-label={t('shared.organization', 'Organization')}
            />
          </div>
        )}

        {/* Transfer type — a cross-org move is always permanent */}
        <div className="mb-3" hidden={isCrossOrg}>
          <label className="form-label">{t('shared.transfer_type', 'Transfer Type')}</label>
          <div className="d-flex gap-2">
            <Button
              type="button"
              color={temporary ? 'light' : 'primary'}
              outline={temporary}
              size="sm"
              onClick={() => setTemporary(false)}
            >
              {t('business.planning.permanent_transfer', 'Permanent Transfer')}
            </Button>
            <Button
              type="button"
              color={temporary ? 'primary' : 'light'}
              outline={!temporary}
              size="sm"
              onClick={() => setTemporary(true)}
            >
              {t('business.planning.temporary_transfer', 'Temporary Transfer')}
            </Button>
          </div>
        </div>

        {/* Destination location */}
        <div className="mb-3">
          <label className="form-label">
            {t('business.planning.transfer_to_location', 'Transfer to Location')} *
          </label>
          {loadingLocations ? (
            <div className="text-muted small">
              <Spinner size="sm" className="me-2" />
              {t('shared.loading', 'Loading...')}
            </div>
          ) : (
            <Select
              options={destinationOptions}
              value={destinationOptions.find((o) => o.value === String(selectedLocationId)) || null}
              onChange={(selected) => handleLocationChange(selected?.value || '')}
              isClearable
              isSearchable
              placeholder={t('shared.search_and_select_location', 'Search and select location...')}
              classNamePrefix="react-select"
            />
          )}
        </div>

        {/* Sublocation (only when the selected location has any) */}
        {selectedLocationId && subLocations.length > 0 && (
          <div className="mb-3">
            <label className="form-label">{t('shared.sublocation', 'Sublocation')}</label>
            <Select
              options={subLocationOptions}
              value={subLocationOptions.find((o) => o.value === String(selectedSubLocationId)) || null}
              onChange={(selected) => setSelectedSubLocationId(selected?.value || '')}
              isClearable
              isSearchable
              placeholder={t('shared.select_sublocation', 'Select Sublocation')}
              classNamePrefix="react-select"
            />
          </div>
        )}

        {/* Start date — a cross-org move executes when the destination accepts it, so it cannot
            be scheduled for later; the field would only offer a value the server rejects. */}
        <div className="mb-3" hidden={isCrossOrg}>
          <label className="form-label">{t('shared.start_date', 'Start Date')} *</label>
          <Input type="datetime-local" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          {scheduled && (
            <span className="text-muted small">
              {t('business.planning.transfer_scheduled_note', 'This transfer is scheduled and will take effect at the selected time.')}
            </span>
          )}
        </div>

        {/* End date (temporary only) */}
        {temporary && (
          <div className="mb-3">
            <label className="form-label">{t('shared.end_date', 'End Date')} *</label>
            <Input type="datetime-local" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            <div className="text-muted small mt-1">
              <i className="bx bx-bell me-1"></i>
              {t('business.planning.auto_return_note', 'The requester and admins are notified 7/3 days before and on the return date.')}
            </div>
          </div>
        )}

        {/* Reason */}
        <div className="mb-3">
          <label className="form-label">{t('shared.reason', 'Reason')}</label>
          <Input
            type="textarea"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('business.planning.transfer_reason_placeholder', 'Reason for the transfer (optional)')}
          />
        </div>

        {/* Impact preview — what this cross-organization move would actually do */}
        {isCrossOrg && (loadingPreview || preview) && (
          <div className="border rounded p-2 mb-2">
            <div className="fw-semibold mb-2">
              {t('business.planning.preview_title', 'What this transfer will do')}
            </div>
            {loadingPreview ? (
              <div className="text-muted small">
                <Spinner size="sm" className="me-2" />
                {t('shared.loading', 'Loading...')}
              </div>
            ) : (
              <>
                {blockers.length > 0 && (
                  <Alert color="danger" className="py-2 mb-2">
                    <div className="fw-semibold mb-1">
                      {t('business.planning.preview_blockers', 'This transfer cannot proceed yet:')}
                    </div>
                    <ul className="mb-0 ps-3">
                      {blockers.map((b) => (
                        <li key={b.code}>
                          {b.message}
                          {b.detail ? ` (${b.detail})` : ''}
                        </li>
                      ))}
                    </ul>
                  </Alert>
                )}
                <ul className="mb-0 ps-3 small">
                  <li>
                    {t('business.planning.preview_assets', '{{roots}} asset(s) and {{subs}} sub-asset(s) will move', {
                      roots: preview.rootAssets?.length || 0,
                      subs: preview.subAssetCount || 0,
                    })}
                  </li>
                  <li>
                    {t('business.planning.preview_history', '{{workOrders}} work order(s) and {{records}} other history record(s) move with them', {
                      workOrders: preview.history?.workOrders || 0,
                      records:
                        (preview.history?.serviceRequests || 0) +
                        (preview.history?.meters || 0) +
                        (preview.history?.calibrations || 0) +
                        (preview.history?.documents || 0) +
                        (preview.history?.statusHistory || 0) +
                        (preview.history?.downtimeLogs || 0),
                    })}
                  </li>
                  {preview.pmChainsToStop?.length > 0 && (
                    <li className="text-warning-emphasis">
                      {t('business.planning.preview_pm_stop', '{{count}} preventive schedule(s) will be stopped', {
                        count: preview.pmChainsToStop.length,
                      })}
                    </li>
                  )}
                  {detachedTotal > 0 && (
                    <li>
                      {t('business.planning.preview_detach', 'The assets will be removed from {{count}} record(s) of this organization (contracts, certificates, parts)', {
                        count: detachedTotal,
                      })}
                    </li>
                  )}
                  {preview.assetTypes?.toCreate?.length > 0 && (
                    <li>
                      {t('business.planning.preview_types_create', 'Asset type(s) created in the destination: {{types}}', {
                        types: preview.assetTypes.toCreate.join(', '),
                      })}
                    </li>
                  )}
                  {preview.customFields?.snapshotted?.length > 0 && (
                    <li>
                      {t('business.planning.preview_fields_snapshot', 'Custom field(s) not defined in the destination will be recorded on the asset timeline and cleared: {{fields}}', {
                        fields: preview.customFields.snapshotted.join(', '),
                      })}
                    </li>
                  )}
                </ul>
              </>
            )}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={toggle} disabled={submitting}>
          {t('shared.cancel', 'Cancel')}
        </Button>
        <Button color="primary" onClick={handleSubmit} disabled={!canSubmit}>
          {submitting && <Spinner size="sm" className="me-2" />}
          {isAdmin && !isCrossOrg
            ? t('business.planning.confirm_transfer', 'Confirm Transfer')
            : t('business.planning.request_transfer', 'Request Transfer')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AssetTransferModal;
